import { useTranslation } from "react-i18next";

import { CreateMaterialLayoutDto } from "#modules/materialLayouts/infra/createMaterialLayout.api";
import { useCreateMaterialLayout } from "#modules/materialLayouts/view/useCreateMaterialLayout";
import { PartDto } from "#modules/parts/infra/fetchPart.api";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { Icon } from "#shared/icons/Icon";
import { Alert } from "#shared/view/components/Alert";
import { Group } from "#shared/view/components/Group";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";
import { Button } from "#shared/view/designSystem/Button";
import { SelectInput } from "#shared/view/designSystem/SelectInput";
import {
  FormatterMaterialParameter,
  getMaterialDensity,
  MaterialGradeType,
  PricingModeSchema,
} from "shared";

import { useDimensionOptions } from "./useDimensionOptions";
import {
  SHOW_OTHER_DIMENSIONS_OPTION,
  useExpandableDimensionOptions,
} from "./useExpandableDimensionOptions";

type Props = {
  openMaterialLayoutModal: () => void;
  materialGrade: MaterialGradeType;
  part: PartDto;
  quoteRequestLine: QuoteRequestLineDto;
  partDimensionsWithOverthickness: FormatterMaterialParameter;
};
export const NoMaterialLayout = ({
  openMaterialLayoutModal,
  materialGrade,
  part,
  quoteRequestLine,
  partDimensionsWithOverthickness,
}: Props) => {
  const { t } = useTranslation();
  const { recommendedOptions, otherOptions, customMaterials } = useDimensionOptions({
    materialGrade,
    materialName: quoteRequestLine.materialName,
    partType: part.type,
    partSubType: part.subType,
    partDimensionsWithOverthickness,
  });

  const { dimensionGroups, expandableSelectProps } = useExpandableDimensionOptions({
    recommendedOptions,
    otherOptions,
  });

  const { createMaterialLayout, isPending } = useCreateMaterialLayout({
    layoutId: quoteRequestLine.layoutId,
    quoteRequestLineId: quoteRequestLine.id,
  });

  const handleChooseMaterialLayout = (value: string | null) => {
    if (isPending || value === SHOW_OTHER_DIMENSIONS_OPTION) {
      return;
    }

    const pickedMaterial = customMaterials.find((m) => m.id === value);

    if (!pickedMaterial) {
      return;
    }
    if (!quoteRequestLine.layoutId) {
      return;
    }

    const partType = pickedMaterial.type;

    const createPayload: CreateMaterialLayoutDto = {
      quoteRequestId: quoteRequestLine.quoteRequestId,
      price: pickedMaterial.price,
      tubeThickness: pickedMaterial.tubeThickness ?? undefined,
      density: pickedMaterial.density ?? getMaterialDensity({ materialGrade }),
      length: pickedMaterial.length,
      width: pickedMaterial.width,
      height: pickedMaterial.height,
      layoutId: quoteRequestLine.layoutId,
      quantity: 1,
      pricingMode: PricingModeSchema.enum.RAW,
      type: partType,
      materialId: pickedMaterial.id,
    };

    createMaterialLayout({
      payload: createPayload,
    });
  };

  return (
    <Stack gap="xs">
      <Alert
        icon={<Icon name="alertTriangle" />}
        p="xs"
        variant="light"
        color="orange"
        title={t("quoteRequestLine.noMaterialLayoutFound")}
      />
      <Group align="flex-end">
        {dimensionGroups.length > 0 && (
          <>
            <SelectInput
              data={dimensionGroups}
              label={t("qrl.material.selectDimensions")}
              onChange={handleChooseMaterialLayout}
              disabled={isPending}
              {...expandableSelectProps}
            />
            <Text>{t("common.or")}</Text>
          </>
        )}
        <Button
          size="xs"
          icon={<Icon name="plus" />}
          onClick={openMaterialLayoutModal}
          disabled={isPending}
        >
          {t("qrl.material.addMaterialLayout")}
        </Button>
      </Group>
    </Stack>
  );
};
